// Customer passkey API client for the marketplace customer embed — Task 8 of
// the 2026-08-30 marketplace-customer-passkeys plan.
//
// Frozen route contract (design §5.2 / dispatch brief — paths + auth only;
// the backend, Task 6, does not exist yet):
//   POST   /api/v1/marketplace/auth/passkey/login-options   public, no email
//   POST   /api/v1/marketplace/auth/passkey/login            public
//   POST   /api/v1/marketplace/me/passkeys/register-options  Bearer JWT
//   POST   /api/v1/marketplace/me/passkeys/register           Bearer JWT
//   GET    /api/v1/marketplace/me/passkeys                    Bearer JWT
//   PATCH  /api/v1/marketplace/me/passkeys/{id}                Bearer JWT
//   DELETE /api/v1/marketplace/me/passkeys/{id}                Bearer JWT
//
// Wire-body ASSUMPTION (documented — Task 6 must confirm or this file needs a
// small, contained adjustment; none of the exported function signatures below
// would change): every response is enveloped `{"data": ...}` — matching the
// existing marketplace auth surface (customer_auth_handler.go's
// request-otp/verify-otp/refresh, all `{"data": {...}}`) and the brief's own
// GET /me/passkeys example, rather than the OPERATOR system-api passkey
// routes, which are unenveloped (design §3.1/R4: a separate, unshared
// implementation). Ceremony correlation between a *-options call and its
// finish call travels as `ceremony_id` in the JSON BODY (not the operator's
// X-Passkey-Session header) since R4 says nothing but the DB table and the
// go-webauthn library is shared, and every existing ecom-api auth route uses
// plain JSON bodies with no custom headers.
//
// FIX-ROUND-2 CRITICAL correction of the fix-round-1 IMPORTANT-1 ruling below:
// `publicKey` is a DIRECT SIBLING of `ceremony_id` on the `data` envelope —
// `{ publicKey: {...}, ceremony_id?: "..." }` — NOT nested under an extra
// `options` wrapper. fix-round-1 mis-read go-webauthn's own marshaling as
// `{ options: { publicKey: {...} } }`; the server (handler's own
// `webauthnOptionsData`, which mirrors protocol.CredentialAssertion /
// CredentialCreation's `{"publicKey": ..., "mediation"?: ...}` shape
// byte-for-byte) never wrapped it in `options` at all — that name only ever
// existed in this client's hand-written interface, not on the wire. The two
// sides disagreed silently because no shared artifact was read by both test
// suites; see __fixtures__/passkey-wire-shapes.json, which now is that shared
// artifact. register-options additionally carries NO `ceremony_id` — the
// registration ceremony is keyed off the caller's authenticated user id, so
// there is nothing for the client to echo back on finish.
//
// The low-level navigator.credentials wrappers (getPasskeyAssertion /
// createPasskeyAttestation) live HERE, MODULE-PRIVATE, not in
// embed-shared/passkey.ts (fix-round-1 ruling R-J — see that file's header
// for why). Only the throwing public functions below are exported.
//
// This client takes a parsed body object, not a string (embedApiFetch
// stringifies it) — matches every sibling *-api.ts file in this directory.

import { embedApiFetch } from "../../../embed-shared/api-client";
import { base64UrlToBytes, bytesToBase64Url, isPasskeySupported } from "../../../embed-shared/passkey";

/**
 * One passkey owned by the authenticated customer, as listed/returned by the
 * server. Exact shape fixed by the Task 8 interface contract — Tasks 9-11
 * import this type. `id` is a STRING (not a DB row number) — treat it as an
 * opaque identifier, never parse or compare numerically.
 */
export interface PasskeySummary {
  id: string;
  friendly_name: string;
  created_at: string;
  last_used_at: string | null;
}

/**
 * Thrown by signInWithPasskey/registerPasskey when the user dismisses the
 * browser's authenticator prompt. Named distinctly from the operator's
 * portal/api/passkey.ts::PasskeyCanceledError (one L, fix-round-1
 * IMPORTANT-4) — the near-identical name was an auto-import footgun: an
 * editor completion could bind a consumer to the WRONG class, making
 * `instanceof` permanently false while typechecking cleanly. `name` is
 * deliberately set to "NotAllowedError" — the same name the browser's own
 * DOMException carries — so a caller can distinguish this from every other
 * failure with a single `err.name === "NotAllowedError"` check, without
 * importing this class at all (Task 9's SignInPanel does exactly that, by
 * design — see its own commit message).
 *
 * A cancelled ceremony is a NORMAL outcome (the user changed their mind or
 * has no enrolled credential), not an error state — callers should fall back
 * to email OTP silently, never render an error banner for it.
 */
export class MarketplacePasskeyCancelledError extends Error {
  constructor() {
    super("Passkey ceremony was cancelled by the user");
    this.name = "NotAllowedError";
  }
}

function rethrowCeremonyFailure(error: unknown, fallbackMessage: string): never {
  if (error instanceof Error) throw error;
  throw new Error(fallbackMessage);
}

// ---------------------------------------------------------------------------
// Low-level ceremony wrappers — MODULE-PRIVATE (fix-round-1 ruling R-J).
// Not exported. If another file ever needs the browser ceremony, add a new
// PUBLIC function below rather than exporting these two.
// ---------------------------------------------------------------------------

interface PasskeyRequestOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: Array<{ id: string; type: string; transports?: string[] }>;
  userVerification?: string;
}

interface PasskeyCreationOptionsJSON {
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  challenge: string;
  pubKeyCredParams: Array<{ type: string; alg: number }>;
  timeout?: number;
  excludeCredentials?: Array<{ id: string; type: string; transports?: string[] }>;
  authenticatorSelection?: {
    authenticatorAttachment?: string;
    requireResidentKey?: boolean;
    residentKey?: string;
    userVerification?: string;
  };
  attestation?: string;
}

/** Serialized assertion — the shape go-webauthn's ParseCredentialRequestResponseBody expects. */
interface PasskeyAssertionJSON {
  id: string;
  rawId: string;
  type: string;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle: string | null;
  };
}

/** Serialized attestation — the shape go-webauthn's ParseCredentialCreationResponseBody expects. */
interface PasskeyAttestationJSON {
  id: string;
  rawId: string;
  type: string;
  response: {
    clientDataJSON: string;
    attestationObject: string;
    transports?: string[];
  };
}

type PasskeyCeremonyOutcome<T> =
  | { status: "ok"; credential: T }
  | { status: "cancelled" }
  | { status: "aborted" }
  | { status: "error"; error: unknown };

/** True for the specific DOMException browsers raise when the user dismisses the authenticator prompt. */
function isUserCancellation(err: unknown): boolean {
  return err instanceof DOMException && err.name === "NotAllowedError";
}

/**
 * True for the DOMException raised when OUR OWN AbortController cancels a
 * ceremony — TD-426's conditional flow aborts the background request the
 * moment the customer commits to email-OTP instead.
 *
 * Kept strictly separate from {@link isUserCancellation}. Both are
 * DOMExceptions and both are non-errors, but they mean opposite things about
 * the USER: `NotAllowedError` is a person declining the authenticator prompt,
 * which is worth surfacing as MarketplacePasskeyCancelledError so a caller can
 * fall back deliberately. `AbortError` is the application withdrawing a
 * request the customer never saw. Collapsing the two would make an ordinary
 * "customer typed their email instead" indistinguishable from "customer
 * dismissed a prompt", and the conditional path fires that abort on every
 * single OTP sign-in.
 */
function isCeremonyAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

/**
 * Thin wrapper over `navigator.credentials.get()` for a discoverable
 * (usernameless) passkey login. Never throws for user cancellation — it is a
 * normal outcome, not an error (the public boundary below converts a
 * "cancelled" outcome into MarketplacePasskeyCancelledError).
 *
 * The "WebAuthn is not supported" error path must be UNREACHABLE in
 * practice — every caller of the public functions is expected to gate on
 * isPasskeySupported() first (Task 9's SignInPanel does). This string is
 * NOT user-facing copy; if it ever reaches a user, a gate was skipped
 * somewhere upstream.
 */
async function getPasskeyAssertion(
  options: PasskeyRequestOptionsJSON,
  // TD-426. `mediation` and `signal` are fields of CredentialRequestOptions —
  // SIBLINGS of `publicKey` in the navigator.credentials.get() argument — not
  // members of the server's options JSON. TD-426's row predicted the blocker
  // here was that PasskeyRequestOptionsJSON "omits the mediation (and
  // extensions) fields the operator-side twin carries". Both halves are wrong,
  // and the fix is deliberately NOT that: there is no operator-side twin
  // carrying mediation (grep: zero hits outside a comment), and widening
  // PasskeyRequestOptionsJSON would place mediation on the SERVER payload,
  // where it would be parsed off the response and never reach the browser API
  // that actually reads it. See the guard test in passkeys.test.ts, which fails
  // if mediation is ever sourced from the server options instead of the caller.
  ceremony: { mediation?: CredentialMediationRequirement; signal?: AbortSignal } = {},
): Promise<PasskeyCeremonyOutcome<PasskeyAssertionJSON>> {
  if (!isPasskeySupported()) {
    return { status: "error", error: new Error("WebAuthn is not supported in this browser") };
  }
  // An already-aborted signal must not open a prompt at all. Browsers reject
  // asynchronously in that case, but checking first keeps the fast path honest
  // and makes the outcome deterministic for callers that abort during setup.
  if (ceremony.signal?.aborted) return { status: "aborted" };

  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: base64UrlToBytes(options.challenge).buffer as ArrayBuffer,
    timeout: options.timeout,
    rpId: options.rpId,
    allowCredentials: options.allowCredentials?.map((c) => ({
      id: base64UrlToBytes(c.id).buffer as ArrayBuffer,
      type: c.type as PublicKeyCredentialType,
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
    userVerification: options.userVerification as UserVerificationRequirement | undefined,
  };

  let credential: PublicKeyCredential;
  try {
    const result = await navigator.credentials.get({
      publicKey,
      // Omitted entirely when undefined, so the non-conditional path sends the
      // byte-identical request it always did.
      ...(ceremony.mediation ? { mediation: ceremony.mediation } : {}),
      ...(ceremony.signal ? { signal: ceremony.signal } : {}),
    });
    if (!result) return { status: "cancelled" };
    credential = result as PublicKeyCredential;
  } catch (err) {
    // Abort BEFORE cancellation: an aborted conditional ceremony is the
    // application withdrawing a request, not a person declining a prompt.
    if (isCeremonyAbort(err)) return { status: "aborted" };
    if (isUserCancellation(err)) return { status: "cancelled" };
    return { status: "error", error: err };
  }

  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    status: "ok",
    credential: {
      id: credential.id,
      rawId: bytesToBase64Url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bytesToBase64Url(response.clientDataJSON),
        authenticatorData: bytesToBase64Url(response.authenticatorData),
        signature: bytesToBase64Url(response.signature),
        userHandle: response.userHandle ? bytesToBase64Url(response.userHandle) : null,
      },
    },
  };
}

/**
 * Thin wrapper over `navigator.credentials.create()` for passkey
 * registration/enrollment. Never throws for user cancellation — see
 * getPasskeyAssertion above. Same unreachable-in-practice note applies to
 * the unsupported-browser error path.
 */
async function createPasskeyAttestation(
  options: PasskeyCreationOptionsJSON,
): Promise<PasskeyCeremonyOutcome<PasskeyAttestationJSON>> {
  if (!isPasskeySupported()) {
    return { status: "error", error: new Error("WebAuthn is not supported in this browser") };
  }

  const publicKey: PublicKeyCredentialCreationOptions = {
    rp: options.rp,
    user: {
      id: base64UrlToBytes(options.user.id).buffer as ArrayBuffer,
      name: options.user.name,
      displayName: options.user.displayName,
    },
    challenge: base64UrlToBytes(options.challenge).buffer as ArrayBuffer,
    pubKeyCredParams: options.pubKeyCredParams.map((p) => ({
      type: p.type as PublicKeyCredentialType,
      alg: p.alg,
    })),
    timeout: options.timeout,
    excludeCredentials: options.excludeCredentials?.map((c) => ({
      id: base64UrlToBytes(c.id).buffer as ArrayBuffer,
      type: c.type as PublicKeyCredentialType,
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
    authenticatorSelection: options.authenticatorSelection
      ? {
          authenticatorAttachment: options.authenticatorSelection.authenticatorAttachment as
            | AuthenticatorAttachment
            | undefined,
          requireResidentKey: options.authenticatorSelection.requireResidentKey,
          residentKey: options.authenticatorSelection.residentKey as ResidentKeyRequirement | undefined,
          userVerification: options.authenticatorSelection.userVerification as
            | UserVerificationRequirement
            | undefined,
        }
      : undefined,
    attestation: options.attestation as AttestationConveyancePreference | undefined,
  };

  let credential: PublicKeyCredential;
  try {
    const result = await navigator.credentials.create({ publicKey });
    if (!result) return { status: "cancelled" };
    credential = result as PublicKeyCredential;
  } catch (err) {
    if (isUserCancellation(err)) return { status: "cancelled" };
    return { status: "error", error: err };
  }

  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    status: "ok",
    credential: {
      id: credential.id,
      rawId: bytesToBase64Url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bytesToBase64Url(response.clientDataJSON),
        attestationObject: bytesToBase64Url(response.attestationObject),
        // CRITICAL fix (fix-round-1): getTransports() is a native WebIDL
        // operation that brand-checks `this` — it MUST be invoked as a
        // method ON the response object, never extracted into a local
        // first (extracting loses the receiver; calling it detached throws
        // "TypeError: Illegal invocation" in every browser that implements
        // it — Chrome 78+, Safari 16.4+, Firefox 119+, Edge). Matches
        // portal/api/passkey.ts's call exactly.
        transports: (response as { getTransports?: () => string[] }).getTransports?.() ?? [],
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Public API — Tasks 9-11 import from here.
// ---------------------------------------------------------------------------

const LOGIN_OPTIONS_PATH = "/api/v1/marketplace/auth/passkey/login-options";
const LOGIN_PATH = "/api/v1/marketplace/auth/passkey/login";
const REGISTER_OPTIONS_PATH = "/api/v1/marketplace/me/passkeys/register-options";
const REGISTER_PATH = "/api/v1/marketplace/me/passkeys/register";
const PASSKEYS_PATH = "/api/v1/marketplace/me/passkeys";

interface PasskeyLoginOptionsResponse {
  ceremony_id: string;
  publicKey: PasskeyRequestOptionsJSON;
}

interface PasskeyRegisterOptionsResponse {
  publicKey: PasskeyCreationOptionsJSON;
}

/** The customer session, identical in shape to the email-OTP verify/refresh result (design §5.2). */
export interface PasskeySessionResult {
  token: string;
  refresh_token?: string;
  expires_in: number;
}

/**
 * Discoverable (usernameless) passkey sign-in: fetch a login challenge that
 * names no email, run the browser ceremony, and exchange the assertion for a
 * customer session.
 *
 * On user cancellation throws {@link MarketplacePasskeyCancelledError}
 * (`name === "NotAllowedError"`) — treat as a normal outcome, fall back to
 * OTP. On any other ceremony failure (unsupported browser, authenticator
 * error) throws the underlying Error. On a server error throws
 * embedApiFetch's ApiError as usual.
 *
 * The FINISH call opts OUT of embedApiFetch's TD-366 silent-retry-on-401
 * (`suppressUnauthenticated: true`, fix-round-1 IMPORTANT-3): the assertion
 * is single-use — the challenge is consumed and the signature counter must
 * strictly increase on first use — so a transparent retry would replay an
 * already-consumed ceremony and get rejected as a replay, orphaning a
 * perfectly good attempt behind an opaque error. Better to fail cleanly and
 * let the caller restart. The *-options call is safely retryable and keeps
 * the default behavior.
 */
export async function signInWithPasskey(): Promise<PasskeySessionResult> {
  const begin = await embedApiFetch<PasskeyLoginOptionsResponse>(LOGIN_OPTIONS_PATH, {
    method: "POST",
    body: {},
  });

  const outcome = await getPasskeyAssertion(begin.publicKey);
  if (outcome.status === "cancelled") throw new MarketplacePasskeyCancelledError();
  // Unreachable on this path — no signal is passed, so nothing can abort the
  // ceremony. Handled rather than cast away so the union stays exhaustive: if
  // a future caller threads a signal through here, this becomes a cancellation
  // instead of silently falling into the finish call with no credential.
  if (outcome.status === "aborted") throw new MarketplacePasskeyCancelledError();
  if (outcome.status === "error") rethrowCeremonyFailure(outcome.error, "Passkey sign-in failed");

  // Login finish sends `assertion`, not `credential` — WebAuthn's own
  // vocabulary (assertion = login, credential/attestation = registration);
  // see passkeyLoginRequest in customer_passkey_handler.go.
  return embedApiFetch<PasskeySessionResult>(LOGIN_PATH, {
    method: "POST",
    body: { ceremony_id: begin.ceremony_id, assertion: outcome.credential },
    suppressUnauthenticated: true,
  });
}

/**
 * TD-426 — CONDITIONAL (autofill) passkey sign-in.
 *
 * Same ceremony as {@link signInWithPasskey}, with two differences that matter:
 *
 * 1. `mediation: "conditional"` — the browser does NOT open a modal. It offers
 *    any discoverable passkey inline in the autofill list of an input carrying
 *    `autocomplete="username webauthn"`, and this promise simply stays pending
 *    until the customer picks one (or the signal aborts). That pending-forever
 *    behaviour is the API working correctly, which is why this must never be
 *    awaited on a path that blocks rendering.
 * 2. It resolves `null` instead of throwing for BOTH abort and user
 *    cancellation. In an autofill context neither is an error the customer
 *    should ever see: aborting is the panel withdrawing a background request
 *    when they commit to OTP, and "cancelling" is them ignoring an autofill
 *    suggestion they never asked for. The explicit-button path keeps throwing
 *    {@link MarketplacePasskeyCancelledError}, because there a dismissed prompt
 *    is a deliberate act the caller should be able to react to.
 *
 * Genuine ceremony and server failures still throw, exactly as on the explicit
 * path — a broken authenticator or a rejected assertion is not silent.
 *
 * The caller MUST own the AbortController and abort on unmount and whenever the
 * customer commits to another sign-in method; a live conditional request left
 * running past its panel keeps an authenticator prompt armed against a form the
 * customer has moved on from.
 */
export async function signInWithPasskeyConditional(
  signal: AbortSignal,
): Promise<PasskeySessionResult | null> {
  if (signal.aborted) return null;

  const begin = await embedApiFetch<PasskeyLoginOptionsResponse>(LOGIN_OPTIONS_PATH, {
    method: "POST",
    body: {},
  });
  // The options round-trip is not abortable, so re-check after it: the customer
  // can easily have chosen OTP while it was in flight, and opening a
  // conditional ceremony after that would arm a prompt nobody is waiting on.
  if (signal.aborted) return null;

  const outcome = await getPasskeyAssertion(begin.publicKey, {
    mediation: "conditional",
    signal,
  });
  if (outcome.status === "aborted" || outcome.status === "cancelled") return null;
  if (outcome.status === "error") rethrowCeremonyFailure(outcome.error, "Passkey sign-in failed");

  return embedApiFetch<PasskeySessionResult>(LOGIN_PATH, {
    method: "POST",
    body: { ceremony_id: begin.ceremony_id, assertion: outcome.credential },
    suppressUnauthenticated: true,
  });
}

/**
 * Enroll a new passkey for the signed-in customer (Bearer JWT required —
 * embedApiFetch attaches it automatically via the registered token provider).
 *
 * On user cancellation throws {@link MarketplacePasskeyCancelledError}.
 * `friendlyName` is sent verbatim (server trims/caps per the operator
 * precedent, migration §5.1 — `friendly_name` is the existing nickname
 * column, no new column). The finish call also opts out of the TD-366 silent
 * retry (fix-round-1 IMPORTANT-3) — see signInWithPasskey's doc comment; the
 * attestation is likewise single-use.
 */
export async function registerPasskey(friendlyName: string): Promise<PasskeySummary> {
  const begin = await embedApiFetch<PasskeyRegisterOptionsResponse>(REGISTER_OPTIONS_PATH, {
    method: "POST",
    body: {},
  });

  const outcome = await createPasskeyAttestation(begin.publicKey);
  if (outcome.status === "cancelled") throw new MarketplacePasskeyCancelledError();
  // Unreachable today — registration passes no AbortSignal, so nothing can
  // abort it. Handled rather than cast away so the union stays exhaustive: if
  // a caller ever threads a signal through, this surfaces as a cancellation
  // instead of falling into the finish call with no credential. TypeScript
  // enforced this the moment "aborted" joined the union (TD-426).
  if (outcome.status === "aborted") throw new MarketplacePasskeyCancelledError();
  if (outcome.status === "error") rethrowCeremonyFailure(outcome.error, "Passkey registration failed");

  // No ceremony_id to echo back here — register-options never sent one (the
  // ceremony is keyed off the caller's authenticated user id server-side).
  return embedApiFetch<PasskeySummary>(REGISTER_PATH, {
    method: "POST",
    body: {
      credential: outcome.credential,
      friendly_name: friendlyName,
    },
    suppressUnauthenticated: true,
  });
}

/**
 * List the authenticated customer's own passkeys. An empty list arrives as
 * `data.passkeys: []` and stays `[]` (never null — project rule §9 family).
 */
export async function listPasskeys(): Promise<PasskeySummary[]> {
  const res = await embedApiFetch<{ passkeys: PasskeySummary[] }>(PASSKEYS_PATH);
  return res.passkeys ?? [];
}

/**
 * Rename a passkey (set its `friendly_name`). Returns the updated record so
 * the caller can re-seed local state directly (rule §59) rather than
 * re-fetching the whole list.
 */
export async function renamePasskey(id: string, friendlyName: string): Promise<PasskeySummary> {
  return embedApiFetch<PasskeySummary>(`${PASSKEYS_PATH}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: { friendly_name: friendlyName },
  });
}

/**
 * Delete OUR SERVER RECORD of a passkey. This does NOT remove the credential
 * from the customer's device/OS keychain (design Ruling R1) — the device may
 * still list it until the customer removes it there themselves. Idempotent
 * and ownership-scoped server-side; deleting an unknown or already-deleted id
 * is expected to be indistinguishable from success (anti-enumeration,
 * operator precedent).
 */
export async function deletePasskey(id: string): Promise<void> {
  await embedApiFetch<void>(`${PASSKEYS_PATH}/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}
